import { useEffect, useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import { Box, Button, TextField } from '@mui/material';
import axiosNest from '../../api/axiosNest';
import { ALERT_BUTTON_SX, OrderAlertCard } from './OrderAlertCard';
import { toErrorMessage } from './orderActionsApi';
import { recordScoped, useStillMounted } from '../../shared/recordScoped';
import { useHasPermission } from '../PermissionGate';
import { useFeature } from '../featureFlags/FeatureGate';

/** Przełącznik połączenia z TMS mimo innego kontrahenta (S-21.10-14). */
export const FEATURE_PRE_ORDER_LINK_OTHER_CONTRACTOR = 'preOrderLinkOtherContractor';

export const CONTRACTOR_LINK_CHECK_TITLE = 'Połączono z TMS mimo innego kontrahenta — sprawdź';

export const CONTRACTOR_LINK_CHECK_ORIGIN =
  'Import z TMS łączy prezlecenie ze zleceniem po numerze zamówienia klienta — także wtedy, gdy kontrahent wybrany ' +
  'na prezleceniu jest inny niż w TMS (decyzja z 21.10.2026). Obowiązuje kontrahent z TMS; historia zlecenia ma wpis ' +
  'z ostrzeżeniem. Sprawdź, czy to ten sam transport i właściwy kontrahent, i potwierdź.';

interface Pending {
  id: number;
  text: string | null;
  at: string;
}

/**
 * Znacznik „połączono mimo innego kontrahenta — do sprawdzenia” (decyzja
 * użytkownika 21.10.2026, pkt 18; API `via/pre-order-link.ts`). Widzi
 * przyjęcie zleceń; „Sprawdziłem” (komentarz opcjonalny) zdejmuje znacznik
 * wpisem w historii. Nic więcej się nie zmienia.
 */
const ContractorLinkCheckCard = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const stillMounted = useStillMounted();
  const enabled = useFeature(FEATURE_PRE_ORDER_LINK_OTHER_CONTRACTOR);
  const canIntake = useHasPermission('ORDER_INTAKE');
  const [pending, setPending] = useState<Pending | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const id = record?.id;
  const applies = enabled && canIntake && id !== undefined && id !== null;

  useEffect(() => {
    if (!applies) return;
    axiosNest
      .get<{ pending: Pending | null }>(`/user-offers/${id}/contractor-link-check`)
      .then(({ data }) => {
        if (stillMounted()) setPending(data?.pending ?? null);
      })
      // Bez odczytu nie ma czego pokazać — karta działa dalej.
      .catch(() => undefined);
  }, [applies, id, stillMounted]);

  if (!applies || !pending) return null;

  const confirm = async () => {
    setBusy(true);
    try {
      await axiosNest.post(`/user-offers/${id}/contractor-link-check`, {
        comment: comment.trim() === '' ? undefined : comment.trim(),
      });
      notify('Zapisano: sprawdzone', { type: 'success' });
      if (stillMounted()) setPending(null);
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      if (stillMounted()) setBusy(false);
    }
  };

  return (
    <Box data-testid="contractor-link-check">
      <OrderAlertCard
        tone="warning"
        title={CONTRACTOR_LINK_CHECK_TITLE}
        detectedAt={[pending.at]}
        origin={CONTRACTOR_LINK_CHECK_ORIGIN}
        lines={[{ id: pending.id, text: pending.text ?? CONTRACTOR_LINK_CHECK_TITLE, detail: null }]}
        note="Potwierdzenie zostaje w historii zlecenia."
        actions={
          <Button
            variant="contained"
            disableElevation
            sx={ALERT_BUTTON_SX}
            disabled={busy}
            data-testid="contractor-link-check-confirm"
            onClick={() => void confirm()}
          >
            Sprawdziłem
          </Button>
        }
      >
        <Box sx={{ mt: 1.5 }}>
          <TextField
            label="Komentarz (opcjonalnie)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            size="small"
            sx={{ width: '100%' }}
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
        </Box>
      </OrderAlertCard>
    </Box>
  );
};

/** Stan należy do rekordu — inna karta zaczyna od zera (`recordScoped`). */
export const ContractorLinkCheckAlert = recordScoped(ContractorLinkCheckCard);
